import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { COLLECTION_SORT_BY } from '../../../shared/common/SortByDropDown';
import { displayCompleteCollection } from '../../../store/header/actions';
import ContentBaseLayout from '../../libx/shared/ContentBaseLayout';
import {
    deselectAll,
    deselectOne,
    isInSelectionMode,
    isSelected,
    selectAll,
    selectOne,
    setSelectionMode
} from '../../../store/productSelection/actions';
import FilterBar from '../../../shared/common/FilterBar';
import ProductList from '../../../shared/common/ProductList';
import Pagination, { getPaginationStateObject } from '../../../shared/common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import ListItem from '../../../shared/common/ListItems/ListItem';
import { addToGroup } from '../../../store/productGroup/actions';
import { AppState } from '../../../store/appState';
import { useOrderLists } from '../../../shared/common/useOrderLists';
import {
    invalidateCompleteCollectionProducts,
    useCompleteCollectionProducts
} from './api/useCompleteCollectionProducts';
import queryString from 'query-string';
import { useQueryParams } from '../../../shared/hooks/useQueryParams';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { deserializeFilter } from '../../../shared/utils/deserializeFilter';
import { useHistory, useLocation, useParams } from 'react-router';
import { getLoanFormat } from '../../../shared/utils/getLoanFormat';
import { AgeCategory, ageCategoryFromString } from '../../../../models/AgeCategory';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import { CompleteCollectionHeader } from './CompleteCollectionHeader';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchCompleteCollectionAggregationsQuickSearch } from '../../../api/collection/complete-collection/fetchCompleteCollectionAggregationsQuickSearch';
import { useLibraryManagedProductGroups } from '../../marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { AuthenticationState } from '../../../store/authentication/state';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchCompleteCollectionAggregations } from '../../../api/collection/complete-collection/fetchCompleteCollectionAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { addProductsToGroup } from '../../../api/library-managed/addProductsToGroup';
import { CollectionProductTile } from '../CollectionProductTile';
import classNames from 'classnames';
import { useAddBulkFromCollectionToCart } from '../../../api/shopping-cart/useAddFromCollectionToCart';
import { SOURCE_COLLECTION } from '../../../store/constants';
import { convertSelectionState } from '../../../shared/product-selection/ProductSelectionTypes';
import { useAddBulkFromCollectionToList } from '../../../api/selection-list/useAddFromCollectionToList';
import { Orderlist } from '../../../../models/OrderList';
import { Article } from '../../../../models/Article';
import { CopyDetailsPopup } from '../copyDetailsPopup/CopyDetailsPopup';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';

const FilterBarWithSpace = styled(FilterBar)`
    padding: 0 0 25px 0;
`;

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';
const DEFAULT_PAGE_SIZE = 54;

export const CompleteCollectionPage = () => {
    const [product, setProduct] = useState<(ProductMetaData & Article) | undefined>(undefined);
    const dispatch = useDispatch();
    const { search } = useLocation();
    const { replace, location } = useHistory();
    const { orderlists, createNewList } = useOrderLists();
    const authentication = useSelector((state: AppState) => state.authentication);
    const productSelection = useSelector((state: AppState) => state.productSelection);
    const isSticky = useSelector((state: AppState) => state.header.isSticky);
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        ageCategory: { value: ageCategoryQueryParam },
        filter: { value: filterQueryParam },
        display: { value: display }
    } = useQueryParams({
        page: 'integer',
        sortBy: 'string',
        pageSize: 'integer',
        ageCategory: 'string',
        filter: 'string',
        display: 'string'
    });

    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    const { loanFormat: loanFormatPathParam } = useParams<{ loanFormat: string }>();
    const loanFormat = getLoanFormat(loanFormatPathParam);
    const ageCategory = ageCategoryFromString(ageCategoryQueryParam);
    const productGroups = useLibraryManagedProductGroups();
    const { products, isLoading: isProductsLoading } = useCompleteCollectionProducts({
        pagination: { page, pageSize, sortBy },
        filterMap: filter,
        loanFormat: loanFormat.requestKey,
        ageCategory
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => completeCollectionPageFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'completeCollectionPage',
            loanFormat.requestKey!,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups
        ],
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchCompleteCollectionAggregations({ ...options, ageCategory, loanFormat: loanFormat.requestKey! }),
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchCompleteCollectionAggregationsQuickSearch({
                ...options,
                ageCategory,
                loanFormat: loanFormat.requestKey!
            }),
        [ageCategory, loanFormat.requestKey]
    );
    //endregion

    useLayoutEffect(() => {
        dispatch(displayCompleteCollection());
    }, [dispatch]);

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(location.search, 'displayFilter', !displayFilter, false);
        replace({ pathname: location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(location.search, 'display', nextDisplayType, false);
        replace({ pathname: location.pathname, search: nextSearch });
    }

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        replace({ pathname: location.pathname, search: nextSearch });
    };

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(location.search, [pageQueryState, sortByQueryState]);

        replace({ pathname: location.pathname, search: nextSearch });
    };

    function onAddToGroup(productGroupId, productId) {
        dispatch(addToGroup(productGroupId, productId));
    }

    function onBulkAddToGroup(productGroupId) {
        dispatch(addProductsToGroup(productGroupId));
    }

    const { isLoading: isLoadingAddProductsToCart, addToCartBulk } = useAddBulkFromCollectionToCart({
        loanFormat,
        filter: filterQueryParam ?? '',
        ageCategory,
        source: SOURCE_COLLECTION,
        callback: () => {
            invalidateCompleteCollectionProducts().then();
        }
    });

    const { isLoading: isLoadingAddProductsToList, addToListBulk } = useAddBulkFromCollectionToList({
        loanFormat,
        filter: filterQueryParam ?? '',
        ageCategory,
        source: SOURCE_COLLECTION
    });

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart(() => {
        invalidateCompleteCollectionProducts().then();
    });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps } = useListContentChecks<SingleAddOptions>({ onAction });

    const onBulkAddToCart = async (performChecks: boolean) => {
        return addToCartBulk(performChecks, convertSelectionState(productSelection, products.count));
    };

    const onBulkAddToList = async (orderlist: Orderlist, performChecks: boolean) => {
        return addToListBulk(orderlist.id, performChecks, convertSelectionState(productSelection, products.count));
    };

    function handleProductSelection(productId) {
        const selected = isSelected(productId, productSelection);
        if (!selected) {
            dispatch(selectOne(productId));
        } else {
            dispatch(deselectOne(productId));
        }
    }

    function handleSelectAll() {
        dispatch(selectAll(products?.count));
    }

    function handleDeselectAll() {
        dispatch(deselectAll());
    }

    function cancelSelectionMode() {
        dispatch(setSelectionMode(false));
    }

    function toggleSelectionMode() {
        dispatch(setSelectionMode(!isInSelectionMode(productSelection)));
    }

    function buildProductsList() {
        if (!isProductsLoading && products && products.count === 0) {
            return null;
        }

        return (
            <div className={classNames({ 'min-h-screen': isProductsLoading }, 'ca-main-inner')}>
                <ProductList
                    displayType={display}
                    products={products.list}
                    orderlists={orderlists}
                    listItemComponent={ListItem}
                    onAddPrice={changePrice}
                    onAddToGroup={onAddToGroup}
                    onBulkAddToCart={onBulkAddToCart}
                    onBulkAddToList={onBulkAddToList}
                    onCreateNewListClicked={createNewList}
                    onPageChange={setPage}
                    onSelectProduct={handleProductSelection}
                    onSortChange={setSortBy}
                    showManagePopup={(product: ProductMetaData & Article) => setProduct(product)}
                    tileItemComponent={CollectionProductTile}
                    productGroups={productGroups}
                    productSelection={productSelection}
                    currentPage={page}
                />
                <Pagination
                    totalItems={products?.count}
                    onPageChange={setPage}
                    currentPage={page}
                    pageSize={pageSize}
                />
            </div>
        );
    }

    function buildCollectionTop() {
        return (
            <>
                <CompleteCollectionHeader
                    isInSelectionMode={isInSelectionMode(productSelection)}
                    onBulkAddToCart={onBulkAddToCart}
                    onBulkAddToList={onBulkAddToList}
                    deselectAll={handleDeselectAll}
                    cancelSelectionMode={cancelSelectionMode}
                    productSelection={productSelection}
                    onBulkAddToGroup={onBulkAddToGroup}
                    isSticky={isSticky}
                    productGroups={productGroups}
                />
                <FilterBarWithSpace
                    toggleSelectionMode={toggleSelectionMode}
                    currentPage={page}
                    selectAll={handleSelectAll}
                    onFilterDisplayToggle={onFilterDisplayToggle}
                    sorting={COLLECTION_SORT_BY}
                    onDisplayChange={onDisplayChange}
                    onSortChange={setSortBy}
                    onPageChange={setPage}
                    hideGroupBy={true}
                    sortBy={sortBy}
                    displayType={display}
                    dispatch={dispatch}
                    totalProducts={products?.count}
                    displayFilter={displayFilter}
                />
            </>
        );
    }

    const loading = isProductsLoading || isLoadingAddProductsToCart || isLoadingAddProductsToList;
    const innerList = buildProductsList();
    const list = <div className="ca-main">{innerList}</div>;

    const topComponent = buildCollectionTop();

    const sidebarComponent = displayFilter ? (
        <ResponsiveFilterBar
            visibleFilterGroups={visibleFilterGroups}
            buildQueryKey={aggregationsQueryKeyBuilder}
            fetchAggregations={fetchAggregations}
            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
        />
    ) : null;

    const collectionIsLoading = loading || false;

    const contentComponent = innerList || collectionIsLoading ? list : <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />;
    return (
        <>
            <ContentBaseLayout
                documentTitle={'Complete Collection | BorrowBox Connect'}
                loading={collectionIsLoading}
                topComponent={topComponent}
                sidebarComponent={sidebarComponent}
                contentComponent={contentComponent}
                modalComponent={
                    product ? (
                        <CopyDetailsPopup
                            onClosePopup={() => setProduct(undefined)}
                            onAddPrice={changePrice}
                            product={product}
                        />
                    ) : null
                }
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
        </>
    );
};

const completeCollectionPageFilterGroups = (authentication: AuthenticationState, ageCategory?: AgeCategory) => [
    ...defaultVisibleFilterGroups(authentication, ageCategory),
    'stockUsageCondition'
];
